import React, { useState, useEffect, useRef, useMemo, useCallback } from 'react';
import ReactDOM from 'react-dom';
import { useTranslation } from 'localization';
import AssigneeMultiSelectorMolecule from '../AssigneeMultiSelector/AssigneeMultiSelectorMolecule';
import { Sphere } from 'types/global';
import type { FilterExpression } from 'types/filters';
import type { GlyphColor } from '../../../types/glyph';
import { useFetchPotentialAssignees } from '@center-platform/spheres/identity';
import PerspectiveAssigneesDisplayAtom from '../../atoms/PerspectiveAssigneesDisplay/PerspectiveAssigneesDisplayAtom';
import { useApiClients } from '../../../context/ApiClientsContext';
import './MultiSelectPerspectiveSelector.scss';

const SEARCH_DEBOUNCE_MS = 750;

interface MultiSelectPerspectiveSelectorProps {
  perspectiveSystemName: string;
  sphere: Sphere;
  organizationId: string;
  onPerspectivesSelected?: (
    codes: string[],
    sphereId: string,
    perspectiveSystemName: string
  ) => void;
  showLabel?: boolean;
  label?: React.ReactNode;
  className?: string;
  isAtlas?: boolean;
  nearbySphereId?: string;
  onAssigneesSelected?: (
    assignees: string[] | null,
    sphereId: string,
    perspectiveSystemName: string,
    assigneeSpheres?: Sphere[]
  ) => void;
  onDone?: (
    sphereId: string,
    perspectiveSystemName: string,
    assignees: string[]
  ) => void;
  isEditing?: boolean;
  stayOpen?: boolean;
  contextFilters?: FilterExpression[] | null;
  displayMode?: 'edit' | 'view' | 'collapse';
  lazy?: boolean;
  /** Caps the avatar stack before collapsing into a `+N` overflow badge. */
  maxVisible?: number;
  /** Text shown in place of the avatar stack when no one is assigned. */
  placeholder?: string;
  /**
   * Whether this column's assignment control is interactive. Mirrors
   * `PerspectiveSelector`'s single-cardinality `assignable` gate — false renders
   * the assignee display read-only (matching the author's toggle and the
   * underlying permission check, which the server enforces regardless).
   */
  assignable?: boolean;
}

// Helper function to extract imageId from sphere properties
const extractImageId = (sphere: any): string | undefined => {
  if (!sphere?.properties?.imageId) return undefined;
  return Array.isArray(sphere.properties.imageId)
    ? sphere.properties.imageId[0]
    : sphere.properties.imageId;
};

// Helper function to map assigned spheres to option format
const mapAssignedToOptions = (assigned: any[]) => {
  return assigned.map(a => ({
    name: a.sphere.properties.name,
    code: a.sphere.id,
    glyph: a.sphere.glyph || '',
    glyphColor: a.sphere.glyphColor,
    imageId: extractImageId(a.sphere),
  }));
};

// Helper function to check if two arrays of options are the same
const areOptionsSame = (options1: any[], options2: any[]): boolean => {
  const ids1 = new Set(options1.map(o => o.code));
  const ids2 = new Set(options2.map(o => o.code));
  return ids1.size === ids2.size && [...ids1].every(id => ids2.has(id));
};

/**
 * MultiSelectPerspectiveSelector:
 * - If perspectiveSystemName is empty, shows "Loading..."
 * - Otherwise, it fetches data via useFetchPotentialAssignees and renders a ListBoxComponent.
 */
const MultiSelectPerspectiveSelector: React.FC<
  MultiSelectPerspectiveSelectorProps
> = React.memo(({
  perspectiveSystemName,
  organizationId,
  sphere,
  onPerspectivesSelected,
  onAssigneesSelected,
  onDone,
  showLabel = false,
  label = '',
  className = '',
  isAtlas = false,
  nearbySphereId = null,
  isEditing = false,
  stayOpen = false,
  contextFilters,
  displayMode = 'collapse',
  lazy,
  maxVisible,
  placeholder: emptyPlaceholder,
  assignable = true,
}) => {
  const { t } = useTranslation();
  const { searchClient } = useApiClients();
  const [isEditingLocal, setIsEditingLocal] = useState(stayOpen);
  const [dropdownPosition, setDropdownPosition] = useState<{ top: number; left: number } | null>(null);
  const nodeRef = useRef<HTMLDivElement>(null);

  // Local filter state for debounced search
  const [localFilter, setLocalFilter] = useState<string>('');
  const [isLoadingMore, setIsLoadingMore] = useState(false);

  // Get initially assigned spheres for this perspective (must be before hook call)
  let initialSelectedOptions: { name: string; code: string; glyph?: string; glyphColor?: GlyphColor; imageId?: string }[] = [];

  if (sphere.assignedPerspectives) {
    const assignedPerspective = sphere.assignedPerspectives.find(
      (p) => p.perspectiveProperties.system_name === perspectiveSystemName
    );

    if (assignedPerspective && assignedPerspective.assigned.length > 0) {
      initialSelectedOptions = mapAssignedToOptions(assignedPerspective.assigned);
    }
  }

  // Use custom hook to fetch potential assignees.
  const { options, isLoading, error, fetchData, hasMore, setSearchTerm, triggerInitialFetch } =
    useFetchPotentialAssignees({
      organizationId,
      schemaSystemName: sphere.properties.type || '',
      perspectiveSystemName,
      nearbySphereId,
      apiClient: searchClient,
      limit: 20,
      selectedOption: initialSelectedOptions.length > 0 ? initialSelectedOptions[0] : null,
      nearbyMode: false,
      contextFilters,
      lazy,
    });

  // Store refs for callbacks to avoid stale closures
  const fetchDataRef = useRef(fetchData);
  const hasMoreRef = useRef(hasMore);
  const isLoadingMoreRef = useRef(isLoadingMore);

  useEffect(() => {
    fetchDataRef.current = fetchData;
    hasMoreRef.current = hasMore;
    isLoadingMoreRef.current = isLoadingMore;
  }, [fetchData, hasMore, isLoadingMore]);

  // Debounced search - triggers API call after delay
  useEffect(() => {
    const delayDebounce = setTimeout(() => {
      setSearchTerm(localFilter);
    }, SEARCH_DEBOUNCE_MS);
    return () => clearTimeout(delayDebounce);
  }, [localFilter, setSearchTerm]);

  // Handle search change from child component
  const handleSearchChange = useCallback((searchTerm: string) => {
    setLocalFilter(searchTerm);
  }, []);

  // Handle load more for infinite scroll
  const handleLoadMore = useCallback(async () => {
    if (isLoadingMoreRef.current || !hasMoreRef.current) {
      return;
    }
    setIsLoadingMore(true);
    try {
      await fetchDataRef.current('append');
    } catch (err) {
      console.error('Error loading more assignees:', err);
    } finally {
      setIsLoadingMore(false);
    }
  }, []);

  // Local state for the user's selection.
  const [selectedOptions, setSelectedOptions] = useState<
    { name: string; code: string; glyph?: string; glyphColor?: GlyphColor; imageId?: string }[] | null
  >(() => {
    return initialSelectedOptions.length > 0 ? initialSelectedOptions : null;
  });

  // Sync selectedOptions with sphere's assignedPerspectives when sphere changes
  useEffect(() => {
    if (!sphere.assignedPerspectives) return;

    const assignedPerspective = sphere.assignedPerspectives.find(
      (p) => p.perspectiveProperties.system_name === perspectiveSystemName
    );

    if (assignedPerspective && assignedPerspective.assigned.length > 0) {
      const syncedOptions = mapAssignedToOptions(assignedPerspective.assigned);

      if (!areOptionsSame(selectedOptions || [], syncedOptions)) {
        setSelectedOptions(syncedOptions);
      }
    } else if (selectedOptions !== null && selectedOptions.length > 0) {
      setSelectedOptions(null);
    }
  }, [sphere.assignedPerspectives, sphere.id, perspectiveSystemName]);

  const handleClick = async () => {
    // Don't allow toggling if stayOpen is true - component should always stay open
    if (stayOpen) return;

    if (!isEditingLocal && (isAtlas || isEditing)) {
      // Calculate position for fixed positioning in Atlas
      if (isAtlas && nodeRef.current) {
        const rect = nodeRef.current.getBoundingClientRect();
        setDropdownPosition({
          top: rect.bottom + 4,
          left: rect.left,
        });
      }
      triggerInitialFetch();
      setIsEditingLocal(true);
    }
  };

  const handleKeyDown = (event: React.KeyboardEvent) => {
    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault();
      handleClick();
    }
  };

  useEffect(() => {
    // Don't set up click-outside listener if stayOpen is true
    if (stayOpen) return;

    const handleDocumentClick = (event: MouseEvent) => {
      const target = event.target as Node;
      const portalDropdown = document.querySelector('.multi-select-portal-dropdown');

      if (
        isEditingLocal &&
        nodeRef.current &&
        !nodeRef.current.contains(target) &&
        !portalDropdown?.contains(target)
      ) {
        setIsEditingLocal(false);
        setDropdownPosition(null);
      }
    };

    document.addEventListener('click', handleDocumentClick);
    return () => {
      document.removeEventListener('click', handleDocumentClick);
    };
  }, [isEditingLocal, stayOpen]);

  // Memoize allAssignedSpheres to prevent unnecessary re-renders
  // IMPORTANT: Must be before any early returns to follow React hooks rules
  const allAssignedSpheres = useMemo(() => {
    if (!sphere.assignedPerspectives) {
      return [];
    }

    const assignedPerspective = sphere.assignedPerspectives.find(
      (p) => p.perspectiveProperties.system_name === perspectiveSystemName
    );

    if (assignedPerspective?.assigned && assignedPerspective.assigned.length > 0) {
      return assignedPerspective.assigned.map(a => a.sphere);
    }

    return [];
  }, [sphere.assignedPerspectives, perspectiveSystemName, sphere.id]);

  // When the selection changes, update local state and notify the parent.
  const handleSelectionChange = (
    value:
      | { name: string; code: string; glyph?: string; glyphColor?: GlyphColor; imageId?: string }[]
      | { name: string; code: string; glyph?: string; glyphColor?: GlyphColor; imageId?: string }
      | null
  ) => {
    let selectedItems: { name: string; code: string; glyph?: string; glyphColor?: GlyphColor; imageId?: string }[] = [];
    if (Array.isArray(value)) {
      selectedItems = value;
    } else if (value) {
      selectedItems = [value];
    }

    // Check if selection actually changed
    if (areOptionsSame(selectedOptions || [], selectedItems)) {
      return;
    }

    setSelectedOptions(selectedItems);
    const codes = selectedItems.map((item) => item.code);

    onPerspectivesSelected?.(codes, sphere.id, perspectiveSystemName);

    // Construct sphere objects from selected items to pass to onAssigneesSelected
    const assigneeSpheres = selectedItems.map((item) => ({
      id: item.code,
      properties: {
        name: item.name,
        imageId: item.imageId,
      },
      glyph: item.glyph,
      glyphColor: item.glyphColor,
      label: item.name,
    } as unknown as Sphere));

    onAssigneesSelected?.(codes, sphere.id, perspectiveSystemName, assigneeSpheres);
  };

  // If perspectiveSystemName is empty, immediately render Loading...
  if (!perspectiveSystemName) {
    return <p>Please choose a perspective</p>;
  }

  // Helper to render assignees display or empty state
  const renderAssigneesDisplay = (wrapperClass?: string) => {
    const shouldShowAvatars = allAssignedSpheres.length > 0;

    let content;
    if (shouldShowAvatars) {
      content = (
        <PerspectiveAssigneesDisplayAtom
          assignees={allAssignedSpheres}
          size="small"
          className="perspective-selector-assignees"
          mode={displayMode}
          maxVisible={maxVisible}
        />
      );
    } else {
      content = (
        <div className="assignee-selector-empty">
          <span className="empty-state">{emptyPlaceholder || t('perspectiveSelector.selectAssignees')}</span>
        </div>
      );
    }

    return wrapperClass ? <div className={wrapperClass}>{content}</div> : content;
  };

  // Render content based on state
  const renderContent = () => {
    // In Atlas, when editing, keep avatars visible in the cell
    // The dropdown will be rendered separately in a portal
    if (isEditingLocal && isAtlas) {
      return renderAssigneesDisplay();
    }

    if (isEditingLocal || stayOpen) {
      const dropdownContent = (
        <div className="perspective-select-container">
          <AssigneeMultiSelectorMolecule
            options={options.map((o) => ({
              name: o.name,
              code: o.code,
              glyph: o.glyph,
              glyphColor: o.glyphColor,
              imageId: o.imageId,
            }))}
            selectedAssignees={selectedOptions || []}
            onSelectionChange={handleSelectionChange}
            onDone={stayOpen ? undefined : () => {
              // Call optional onDone callback with current assignees
              if (onDone) {
                const assigneeIds = (selectedOptions || []).map(opt => opt.code);
                onDone(sphere.id, perspectiveSystemName, assigneeIds);
              }
              setIsEditingLocal(false);
              setDropdownPosition(null);
            }}
            isEditing={true}
            placeholder="Search people or roles..."
            showNearbyToggle={!stayOpen}
            onSearchChange={handleSearchChange}
            onLoadMore={handleLoadMore}
            hasMore={hasMore}
            isLoadingMore={isLoadingMore || isLoading}
            searchValue={localFilter}
          />
          {error && <p style={{ color: 'red' }}>Error: {error}</p>}
        </div>
      );

      return dropdownContent;
    }

    if (isEditing && !isAtlas) {
      return renderAssigneesDisplay('perspective-selector-editable-preview');
    }

    return renderAssigneesDisplay();
  };

  // Render dropdown portal separately for Atlas
  const renderDropdownPortal = () => {
    if (!isAtlas || !isEditingLocal || !dropdownPosition) {
      return null;
    }

    const dropdownContent = (
      <div className="perspective-select-container">
        <AssigneeMultiSelectorMolecule
          options={options.map((o) => ({
            name: o.name,
            code: o.code,
            glyph: o.glyph,
            glyphColor: o.glyphColor,
            imageId: o.imageId,
          }))}
          selectedAssignees={selectedOptions || []}
          onSelectionChange={handleSelectionChange}
          onDone={() => {
            if (onDone) {
              const assigneeIds = (selectedOptions || []).map(opt => opt.code);
              onDone(sphere.id, perspectiveSystemName, assigneeIds);
            }
            setIsEditingLocal(false);
            setDropdownPosition(null);
          }}
          isEditing={true}
          placeholder="Search people or roles..."
          showNearbyToggle={true}
          onSearchChange={handleSearchChange}
          onLoadMore={handleLoadMore}
          hasMore={hasMore}
          isLoadingMore={isLoadingMore || isLoading}
          searchValue={localFilter}
        />
        {error && <p style={{ color: 'red' }}>Error: {error}</p>}
      </div>
    );

    return ReactDOM.createPortal(
      <div
        className="multi-select-portal-dropdown"
        style={{
          position: 'fixed',
          top: `${dropdownPosition.top}px`,
          left: `${dropdownPosition.left}px`,
          zIndex: 9999,
          minWidth: '300px',
          maxWidth: '400px',
        }}
      >
        {dropdownContent}
      </div>,
      document.body
    );
  };

  const canInteract = assignable && (isAtlas || isEditing) && !stayOpen && !isEditingLocal;

  return (
    <>
      <div
        ref={nodeRef}
        className={`multi-select-perspective-container ${className} ${isEditingLocal ? 'editing' : ''} ${isAtlas ? 'in-atlas' : ''}`}
      >
        {showLabel && (
          <label className="perspective-label">
            {label || sphere.properties.name}
          </label>
        )}
        {canInteract ? (
          <button
            type="button"
            className="perspective-selector-interactive-trigger"
            onClick={(e) => {
              e.stopPropagation();
              handleClick();
            }}
            onKeyDown={handleKeyDown}
          >
            {renderContent()}
          </button>
        ) : (
          renderContent()
        )}
      </div>
      {renderDropdownPortal()}
    </>
  );
});

MultiSelectPerspectiveSelector.displayName = 'MultiSelectPerspectiveSelector';

export default MultiSelectPerspectiveSelector;
